import { STANDARD_WINDOW_MS } from "./window";

/**
 * Decision rows 157 and 158 (OWEDECHO, loop k): the draft-mode echo.
 *
 * Draft mode is the production path: the system drafts, Mike approves, and
 * Mike hand-sends the approved text from the Meta inbox, so the send comes
 * back as an owner-typed row. The owed read (packages/db/src/owedAnswer.ts)
 * must tell that echo from the shop saying something else, and it used to do
 * so byte for byte: a light edit in the inbox (case, spacing, a curly
 * apostrophe, a full stop) read as "the shop said something else" and ended
 * the promise, dropping the nudge hold, the campaign hold, the ghost-label
 * hold and both owed pages. The failure ran toward "answered".
 *
 * Every rule here may only keep a thread owed LONGER than the byte compare
 * did, never shorter: two texts equal byte for byte are equal after
 * `normalizeEchoText`, and `releasedDraftAnsweredSince` answers only when
 * some message after the release differs even after it.
 */

// Built from code points, never typed as glyphs, so the table reads the same
// in any editor and the prose scan never sees a stray dash.
const cp = (...points: number[]): string => String.fromCodePoint(...points);

const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const LOWER = "abcdefghijklmnopqrstuvwxyz";
/** Curly and prime single quotes, and the modifier apostrophe phones type. */
const SINGLE_QUOTES = cp(0x2018, 0x2019, 0x201a, 0x201b, 0x2032, 0x02bc);
/** Curly and prime double quotes. */
const DOUBLE_QUOTES = cp(0x201c, 0x201d, 0x201e, 0x201f, 0x2033);
/** JavaScript's `\s` set, spelled out so SQL folds exactly the same code points. */
const WHITESPACE = cp(
  0x09, 0x0a, 0x0b, 0x0c, 0x0d, 0xa0, 0x1680,
  0x2000, 0x2001, 0x2002, 0x2003, 0x2004, 0x2005, 0x2006, 0x2007, 0x2008, 0x2009, 0x200a,
  0x2028, 0x2029, 0x202f, 0x205f, 0x3000, 0xfeff,
);

/**
 * The fold, one code point to one code point, in the shape SQL's
 * `translate(text, from, to)` takes: ASCII capitals to lowercase, curly quotes
 * to straight ones, every whitespace character to a plain space.
 *
 * Lowercase is ASCII only on purpose. SQL `lower()` follows the database
 * collation, which differs between PGlite and a hosted Postgres; `translate`
 * with this table does not. A capital outside ASCII therefore stays a
 * difference, which reads as "not the echo", the byte compare's reading.
 */
export const ECHO_FOLD_FROM = `${UPPER}${SINGLE_QUOTES}${DOUBLE_QUOTES}${WHITESPACE}`;
export const ECHO_FOLD_TO = `${LOWER}${"'".repeat(Array.from(SINGLE_QUOTES).length)}${'"'.repeat(
  Array.from(DOUBLE_QUOTES).length,
)}${" ".repeat(Array.from(WHITESPACE).length)}`;

/** Trimmed off the end after the fold: trailing punctuation and the spaces between it (SQL `rtrim(text, set)`). */
export const ECHO_TRAILING_STRIP = `.!?,;:${cp(0x2026)} `;

const FOLD: ReadonlyMap<string, string> = (() => {
  const from = Array.from(ECHO_FOLD_FROM);
  const to = Array.from(ECHO_FOLD_TO);
  if (from.length !== to.length) throw new Error("textEcho: the fold table is not one to one");
  return new Map(from.map((ch, i) => [ch, to[i]!]));
})();
const TRAILING: ReadonlySet<string> = new Set(Array.from(ECHO_TRAILING_STRIP));

/**
 * The one normalization the echo compare uses (row 157): fold (ASCII case,
 * curly quotes, whitespace), collapse runs of spaces and trim both ends, then
 * strip trailing punctuation. The SQL in packages/db/src/owedAnswer.ts runs
 * the same steps from the same three constants (`translate`, then
 * `string_to_array` on a space with the empties removed, then `rtrim`), and a
 * PGlite test there pins the two equal on the same inputs. No regex and no
 * LIKE touch the text on either side.
 */
export function normalizeEchoText(text: string): string {
  let folded = "";
  for (const ch of text) folded += FOLD.get(ch) ?? ch;
  const collapsed = folded
    .split(" ")
    .filter((word) => word !== "")
    .join(" ");
  let end = collapsed.length;
  while (end > 0 && TRAILING.has(collapsed[end - 1]!)) end -= 1;
  return collapsed.slice(0, end);
}

/**
 * Row 157's "treat the first shop message after release as the echo": the
 * first shop or agent message after the owner released a draft counts as the
 * hand-send of that draft, however Mike edited it, when it is an owner-typed
 * (`shop`) row sent within this long of the customer's last message before it,
 * that is inside the Meta window it was sent in.
 *
 * AWAITING RATIFICATION (loop k item OWEDECHO): the standard 24h window as the bound for row 157's first-message echo is an implementer reading, not a figure Kabir has seen.
 */
export const RELEASE_ECHO_WINDOW_MS = STANDARD_WINDOW_MS;

/**
 * What the owed read saw after the owner released a draft. Flags only: the
 * bodies are compared inside SQL and never leave it (CLAUDE.md section 10).
 */
export interface ReleaseEchoFacts {
  /** The first shop or agent message after the release exists and its body differs from the released text after `normalizeEchoText`. */
  readonly firstMessageDiffers: boolean;
  /** That first message is an owner-typed (`shop`) row sent within `RELEASE_ECHO_WINDOW_MS` of the customer's last message before it. */
  readonly firstIsShopInWindow: boolean;
  /** Some shop or agent message after the release, other than the first, differs from the released text after `normalizeEchoText`. */
  readonly laterMessageDiffers: boolean;
}

/**
 * Has the shop said something else since the owner released the draft? A
 * message that normalizes equal to the released text is the echo, and so is
 * the first owner-typed message after the release in its window, edited or
 * not (row 157). Anything else that went out after the release answers: the
 * owner replied, so the released draft is no longer the last word.
 *
 * A customer turn after the release changes nothing here (row 158): only a
 * shop or agent message can answer.
 */
export function releasedDraftAnsweredSince(f: ReleaseEchoFacts): boolean {
  if (f.laterMessageDiffers) return true;
  return f.firstMessageDiffers && !f.firstIsShopInWindow;
}
